import type { FC, FormEvent, ReactNode } from 'react'
import type { AttendanceStatus, ClassRoom, Gender, Student } from '@/types'
import type { ImportStudentRow } from '@/components/providers/academic-provider'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button, ButtonLink } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { Avatar } from '@/components/ui/feedback'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { SearchField } from '@/components/app/search-field'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { downloadXlsx, readSpreadsheetFile } from '@/lib/spreadsheet'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import {
    ArrowRight,
    Download,
    FileSpreadsheet,
    Info,
    Pencil,
    Save,
    Trash2,
    Upload,
    UserPlus,
    Users
} from 'lucide-react'

const NEW_CLASS = '__baru__'

const GENDER_LABELS: Record<Gender, string> = {
    L: 'Laki-laki',
    P: 'Perempuan'
}

// Same column detection and row rules as the original importer
const normalizeHeader = (value: string): string => value.toLowerCase().replace(/[^a-z]/g, '')

const detectColumns = (row: string[]): (number | null)[] | null => {
    const labels = row.map(normalizeHeader)
    const find = (names: string[]): number | null => {
        const index = labels.findIndex(label => names.includes(label))

        return index >= 0 ? index : null
    }

    const nis = find(['nis', 'nisn', 'nomorinduk'])
    const name = find(['nama', 'namasiswa', 'namalengkap'])

    if (nis === null || name === null) return null

    return [nis, name, find(['kelas', 'namakelas']), find(['jeniskelamin', 'gender', 'jk']), find(['status', 'kehadiran', 'absensi'])]
}

const prepareStudentRows = (rows: string[][]): { prepared: ImportStudentRow[], skipped: number } => {
    let columns: (number | null)[] = [0, 1, null, null, null]
    let body = rows

    const headerIndex = rows.findIndex(row => detectColumns(row) !== null)

    if (headerIndex >= 0) {
        columns = detectColumns(rows[headerIndex]) as (number | null)[]
        body = rows.filter((_, index) => index !== headerIndex)
    }

    const read = (row: string[], column: number | null): string => column === null ? '' : (row[column] ?? '').trim()
    const prepared: ImportStudentRow[] = []
    let skipped = 0

    body.forEach(row => {
        if (row.every(cell => cell.trim() === '')) return

        const nis = read(row, columns[0])
        const name = read(row, columns[1])
        const className = read(row, columns[2])

        if (!nis && !name && !className) return

        if (!nis || !name || nis.toLowerCase() === 'nis' || !/[\p{L}\p{N}]/u.test(nis) || !/\p{L}/u.test(name) || nis.length > 30 || name.length > 100) {
            skipped += 1

            return
        }

        if (!className || className.length > 50) {
            skipped += 1

            return
        }

        const rawGender = read(row, columns[3]).toLowerCase()
        const gender: Gender | null | false = ['', '-'].includes(rawGender)
            ? null
            : ['l', 'laki-laki', 'laki laki', 'laki', 'pria', 'male'].includes(rawGender)
                ? 'L'
                : ['p', 'perempuan', 'wanita', 'female'].includes(rawGender) ? 'P' : false

        const rawStatus = read(row, columns[4]).toLowerCase()
        const statusMap: Record<string, AttendanceStatus | null> = { '': null, h: 'H', hadir: 'H', s: 'S', sakit: 'S', i: 'I', izin: 'I', a: 'A', alpa: 'A', alfa: 'A', absen: 'A' }
        const status = rawStatus in statusMap ? statusMap[rawStatus] : false

        if (gender === false || status === false) {
            skipped += 1

            return
        }

        prepared.push({ nis, name, className, gender, status })
    })

    return { prepared, skipped }
}

type StudentFormProps = {
    classes: ClassRoom[]
    defaultClassId: string
    editing: Student | null
    onDone: () => void
}

const StudentForm: FC<StudentFormProps> = ({ classes, defaultClassId, editing, onDone }): ReactNode => {
    const { actions } = useAcademic()
    const toast = useToast()

    const [nis, setNis] = useState(editing?.nis ?? '')
    const [name, setName] = useState(editing?.name ?? '')
    const [gender, setGender] = useState<string>(editing?.gender ?? '')
    const [classChoice, setClassChoice] = useState(editing?.classId ?? (classes.length === 0 ? NEW_CLASS : defaultClassId))
    const [newClassName, setNewClassName] = useState('')

    const creatingClass = classChoice === NEW_CLASS

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.saveStudent({
            id: editing?.id,
            nis,
            name,
            gender: gender === 'L' || gender === 'P' ? gender : null,
            classId: creatingClass ? null : classChoice,
            newClassName: creatingClass ? newClassName : ''
        })

        if (!result.ok) {
            toast.error('Gagal menyimpan', result.error)

            return
        }

        toast.success(result.message ?? 'Data siswa berhasil disimpan.')

        if (!editing) {
            setNis('')
            setName('')
            setGender('')
            setNewClassName('')
        }

        onDone()
    }

    return (
        <form onSubmit={handleSubmit} className='grid gap-4 p-5 sm:grid-cols-2'>
            <Field label='NIS Siswa' htmlFor='studentNis'>
                <Input id='studentNis' value={nis} onChange={event => setNis(event.target.value)} maxLength={30} placeholder='Contoh: 2026101' required />
            </Field>

            <Field label='Nama Lengkap' htmlFor='studentName'>
                <Input id='studentName' value={name} onChange={event => setName(event.target.value)} maxLength={100} placeholder='Contoh: Ahmad Fauzi' required />
            </Field>

            <Field label='Jenis Kelamin' htmlFor='studentGender'>
                <Select id='studentGender' value={gender} onChange={event => setGender(event.target.value)}>
                    <option value=''>Pilih</option>
                    <option value='L'>Laki-laki</option>
                    <option value='P'>Perempuan</option>
                </Select>
            </Field>

            <Field label='Kelas' htmlFor='studentClassId'>
                <Select id='studentClassId' value={classChoice} onChange={event => setClassChoice(event.target.value)}>
                    <option value={NEW_CLASS}>+ Tambah kelas baru</option>
                    {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Select>
            </Field>

            {creatingClass && (
                <Field label='Nama kelas baru' htmlFor='studentClassName' className='sm:col-span-2'>
                    <Input
                        id='studentClassName'
                        value={newClassName}
                        onChange={event => setNewClassName(event.target.value)}
                        placeholder='Contoh: XI RPL 1'
                        maxLength={50}
                        required
                    />
                </Field>
            )}

            <div className='flex flex-col gap-2 sm:col-span-2'>
                <Button type='submit' className='w-full'>
                    {editing ? <Save aria-hidden='true' /> : <UserPlus aria-hidden='true' />}
                    {editing ? 'Simpan Perubahan' : 'Tambah Siswa'}
                </Button>

                {editing && (
                    <Button variant='ghost' size='sm' onClick={onDone}>
                        Batal mengubah siswa
                    </Button>
                )}
            </div>
        </form>
    )
}

const StudentsPage: FC = (): ReactNode => {
    const { classes, students, classById, studentById, actions } = useAcademic()
    const { ready, get, set } = useQueryParams()
    const confirm = useConfirm()
    const toast = useToast()

    const [query, setQuery] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [importing, setImporting] = useState(false)
    const [fileInputKey, setFileInputKey] = useState(0)

    if (!ready) return <PageSkeleton />

    const requestedClass = get('kelas')
    const classId = classes.some(item => item.id === requestedClass) ? requestedClass as string : classes[0]?.id ?? ''
    const currentClass = classById.get(classId)
    const editing = studentById.get(get('edit') ?? '') ?? null
    const rows = students.filter(student => student.classId === classId)

    const search = query.trim().toLowerCase()
    const visibleRows = rows.filter(student => `${student.nis} ${student.name}`.toLowerCase().includes(search))

    const handleDelete = async (student: Student): Promise<void> => {
        const approved = await confirm({
            title: 'Hapus siswa dan catatan akademiknya?',
            description: `${student.name} beserta seluruh catatan absensi dan nilainya akan dihapus permanen.`,
            confirmLabel: 'Ya, hapus'
        })

        if (!approved) return

        const result = actions.deleteStudent(student.id)

        if (result.ok) toast.success(result.message ?? 'Siswa dihapus.')
        if (editing?.id === student.id) set({ edit: null })
    }

    const handleTemplate = async (): Promise<void> => {
        try {
            await downloadXlsx('template-import-siswa.xlsx', [
                ['No', 'NIS', 'Nama', 'Jenis Kelamin', 'Kelas'].map(value => ({ value, header: true })),
                ...Array.from({ length: 36 }, (_, index) => [{ value: index + 1 }, { value: '' }, { value: '' }, { value: '' }, { value: '' }])
            ], { sheet: 'Siswa', columns: [6, 16, 32, 16, 14], headerRows: 1 })
        }
        catch {
            toast.error('Template gagal dibuat', 'Coba unduh sekali lagi.')
        }
    }

    const handleImport = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!file) {
            toast.error('Pilih file terlebih dahulu', 'Gunakan file CSV, XML Spreadsheet, atau Excel (.xlsx).')

            return
        }

        setImporting(true)

        try {
            const { prepared, skipped } = prepareStudentRows(await readSpreadsheetFile(file))
            const result = actions.importStudents(prepared, skipped)

            if (!result.ok) {
                toast.error('Impor gagal', result.error)

                return
            }

            toast.success('Impor selesai', result.message)
            setFile(null)
            setFileInputKey(key => key + 1)

            if (result.classId) set({ kelas: result.classId, edit: null })
        }
        catch (error) {
            toast.error('Impor gagal', error instanceof Error ? error.message : 'File tidak dapat dibaca.')
        }
        finally {
            setImporting(false)
        }
    }

    return (
        <>
            <Seo title='Siswa' />

            <PageHeader
                eyebrow='Direktori sekolah'
                title='Data Siswa'
                description='Kelola data siswa per kelas, tambah siswa baru, atau import dari file Excel / CSV.'
                actions={(
                    <div className='w-full sm:w-60'>
                        <label htmlFor='studentClass' className='mb-1.5 block text-xs font-semibold text-muted-foreground'>
                            Filter kelas
                        </label>

                        <Select
                            id='studentClass'
                            value={classId}
                            disabled={classes.length === 0}
                            onChange={event => set({ kelas: event.target.value, edit: null })}
                        >
                            {classes.length === 0 && <option value=''>Belum ada kelas</option>}
                            {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </div>
                )}
            />

            {classes.length === 0 && (
                <div className='mb-6 flex items-start gap-3 rounded-xl border border-info/25 bg-info/8 px-4 py-3 text-sm text-foreground' role='status'>
                    <Info className='mt-0.5 size-4 shrink-0 text-info' aria-hidden='true' />
                    Belum ada kelas. Tulis nama kelas saat menambahkan siswa atau masukkan di file impor.
                </div>
            )}

            <Card className='mb-6 flex flex-col gap-4 p-4 sm:flex-row sm:items-center'>
                <span className='grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary' aria-hidden='true'>
                    <Users className='size-5' />
                </span>

                <div className='min-w-0 flex-1'>
                    <p className='text-[15px] font-bold text-foreground'>
                        {rows.length} Siswa Terdaftar
                    </p>

                    <p className='text-[13px] text-muted-foreground'>
                        Kelas {currentClass?.name ?? 'belum dipilih'}
                    </p>
                </div>

                {classId && (
                    <ButtonLink href={`/absen?kelas=${classId}`} variant='soft' size='sm'>
                        Catat Absensi Kelas Ini
                        <ArrowRight aria-hidden='true' />
                    </ButtonLink>
                )}
            </Card>

            <div className='grid items-start gap-6 xl:grid-cols-2'>
                <Card className={cn(editing && 'ring-2 ring-primary/30')}>
                    <CardHeader
                        icon={editing ? <Pencil /> : <UserPlus />}
                        title={editing ? 'Ubah Data Siswa' : 'Tambah Siswa'}
                        description='Isi NIS dan nama lengkap siswa di bawah ini.'
                    />

                    <StudentForm
                        key={editing?.id ?? `baru-${classId}`}
                        classes={classes}
                        defaultClassId={classId}
                        editing={editing}
                        onDone={() => set({ edit: null })}
                    />
                </Card>

                <Card>
                    <CardHeader
                        icon={<FileSpreadsheet />}
                        title='Import Banyak Siswa'
                        description='Unggah file Excel (.xlsx) atau CSV sesuai template.'
                        action={(
                            <Button variant='outline' size='sm' onClick={() => void handleTemplate()}>
                                <Download aria-hidden='true' />
                                Unduh Template Excel
                            </Button>
                        )}
                    />

                    <form onSubmit={event => void handleImport(event)} className='space-y-4 p-5'>
                        <label
                            htmlFor='studentFile'
                            className='flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-input bg-muted/30 px-4 py-7 text-center transition-colors hover:border-primary/40 hover:bg-primary/5 has-[:focus-visible]:border-ring has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/20'
                        >
                            <span className='grid size-11 place-items-center rounded-full bg-primary/10 text-primary' aria-hidden='true'>
                                <Upload className='size-5' />
                            </span>

                            <span className='text-sm font-semibold text-foreground'>
                                {file ? file.name : 'Pilih file CSV atau Excel siswa'}
                            </span>

                            <span className='text-xs text-muted-foreground'>
                                {file ? `${Math.max(1, Math.round(file.size / 1024))} KB · klik untuk mengganti file` : 'Format .xlsx, .csv, atau XML Spreadsheet'}
                            </span>

                            <input
                                key={fileInputKey}
                                id='studentFile'
                                type='file'
                                accept='.csv,.xml,.xlsx,text/csv,text/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                                className='sr-only'
                                onChange={event => setFile(event.target.files?.[0] ?? null)}
                            />
                        </label>

                        <Button type='submit' className='w-full' loading={importing}>
                            <Upload aria-hidden='true' />
                            Import Sekarang
                        </Button>

                        <p className='text-xs leading-relaxed text-muted-foreground'>
                            Template berisi kolom <strong className='font-semibold text-foreground'>No, NIS, Nama, Jenis Kelamin, Kelas</strong>. Kelas baru dibuat otomatis jika belum tersedia.
                        </p>
                    </form>
                </Card>
            </div>

            <Card className='mt-6'>
                <CardHeader
                    eyebrow={`Direktori kelas ${currentClass?.name ?? ''}`}
                    title='Daftar Siswa'
                    description={`${rows.length} siswa terdaftar`}
                    action={(
                        <SearchField
                            label='Cari siswa'
                            placeholder='Cari nama atau NIS...'
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            wrapperClassName='w-full sm:w-64'
                        />
                    )}
                />

                <Table>
                    <THead>
                        <tr>
                            <TH className='w-14'>No.</TH>
                            <TH>NIS</TH>
                            <TH>Nama Siswa</TH>
                            <TH>Jenis Kelamin</TH>
                            <TH>Kelas</TH>
                            <TH className='text-right'>Aksi</TH>
                        </tr>
                    </THead>

                    <TBody>
                        {visibleRows.map(student => (
                            <TR key={student.id} className={cn(editing?.id === student.id && 'bg-primary/5')}>
                                <TD className='font-semibold text-muted-foreground tabular-nums'>{rows.indexOf(student) + 1}</TD>
                                <TD>
                                    <span className='rounded-md bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground'>
                                        {student.nis}
                                    </span>
                                </TD>
                                <TD>
                                    <span className='flex items-center gap-3'>
                                        <Avatar name={student.name} className='size-8 text-[11px]' />
                                        <span className='font-semibold text-foreground'>{student.name}</span>
                                    </span>
                                </TD>
                                <TD>
                                    {student.gender ? (
                                        <Badge tone={student.gender === 'L' ? 'info' : 'primary'}>{GENDER_LABELS[student.gender]}</Badge>
                                    ) : (
                                        <span className='text-muted-foreground'>—</span>
                                    )}
                                </TD>
                                <TD>{classById.get(student.classId)?.name ?? '-'}</TD>
                                <TD>
                                    <div className='flex justify-end gap-2'>
                                        <Button variant='outline' size='xs' onClick={() => set({ edit: student.id })}>
                                            <Pencil aria-hidden='true' />
                                            Edit
                                        </Button>

                                        <Button variant='danger-outline' size='xs' onClick={() => void handleDelete(student)}>
                                            <Trash2 aria-hidden='true' />
                                            Hapus
                                        </Button>
                                    </div>
                                </TD>
                            </TR>
                        ))}

                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className='px-5 py-12 text-center text-sm text-muted-foreground'>
                                    Belum ada siswa di kelas ini.
                                </td>
                            </tr>
                        )}

                        {rows.length > 0 && visibleRows.length === 0 && (
                            <tr>
                                <td colSpan={6} className='px-5 py-12 text-center text-sm text-muted-foreground'>
                                    Tidak ada siswa yang cocok dengan pencarian.
                                </td>
                            </tr>
                        )}
                    </TBody>
                </Table>
            </Card>
        </>
    )
}

export default StudentsPage
